const MAX_SERVER_DOUBLE_ABS_TEXT = '999999999999999';
const SERVER_DOUBLE_FRACTION_DIGITS = 5;
const MAX_FORMATTED_DECIMAL_DIGITS = 1000;

export class ServerNumberRangeError extends RangeError {
    constructor() {
        super('Server number exceeds database range');
        this.name = 'ServerNumberRangeError';
    }
}

function expandDecimal(value) {
    const text = String(value).trim();
    const match = /^(-?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(text);
    if (match == null) {
        return {negative: false, integer: '0', fraction: ''};
    }
    const negative = match[1] === '-';
    const integer = match[2];
    const fraction = match[3] || '';
    const exponent = Number(match[4] || 0);
    if (!Number.isSafeInteger(exponent)) {
        throw new Error('Server number exponent is invalid');
    }
    const digits = integer + fraction;
    const decimalPosition = integer.length + exponent;
    const expandedIntegerLength = Math.max(decimalPosition, 1);
    const expandedFractionLength = decimalPosition <= 0
        ? -decimalPosition + digits.length
        : Math.max(digits.length - decimalPosition, 0);
    if (digits.length > MAX_FORMATTED_DECIMAL_DIGITS
        || expandedIntegerLength > MAX_FORMATTED_DECIMAL_DIGITS
        || expandedFractionLength > MAX_FORMATTED_DECIMAL_DIGITS) {
        throw new Error('Decimal number is too large to format');
    }
    if (decimalPosition <= 0) {
        return {negative, integer: '0', fraction: '0'.repeat(-decimalPosition) + digits};
    }
    if (decimalPosition >= digits.length) {
        return {negative, integer: digits + '0'.repeat(decimalPosition - digits.length), fraction: ''};
    }
    return {
        negative,
        integer: digits.substring(0, decimalPosition),
        fraction: digits.substring(decimalPosition)
    };
}

function normalizeInteger(integer) {
    const normalized = integer.replace(/^0+(?=\d)/, '');
    return normalized === '' ? '0' : normalized;
}

function exceedsMaximum(integer, fraction) {
    if (integer.length !== MAX_SERVER_DOUBLE_ABS_TEXT.length) {
        return integer.length > MAX_SERVER_DOUBLE_ABS_TEXT.length;
    }
    if (integer !== MAX_SERVER_DOUBLE_ABS_TEXT) {
        return integer > MAX_SERVER_DOUBLE_ABS_TEXT;
    }
    return /[1-9]/.test(fraction);
}

function roundedNumberParts(value, fractionDigits, enforceServerRange) {
    if (!Number.isInteger(fractionDigits)
        || fractionDigits < 0
        || fractionDigits > SERVER_DOUBLE_FRACTION_DIGITS) {
        throw new Error('Server number fraction digits are invalid');
    }
    const decimal = expandDecimal(value);
    let integer = normalizeInteger(decimal.integer);
    if (enforceServerRange && exceedsMaximum(integer, decimal.fraction)) {
        throw new ServerNumberRangeError();
    }
    let fraction = decimal.fraction.substring(0, fractionDigits).padEnd(fractionDigits, '0');
    if (decimal.fraction.length > fractionDigits
            && decimal.fraction.charAt(fractionDigits) >= '5') {
        const rounded = incrementDecimal(integer, fraction, fractionDigits);
        integer = rounded.integer;
        fraction = rounded.fraction;
    }
    if (enforceServerRange && exceedsMaximum(integer, fraction)) {
        throw new ServerNumberRangeError();
    }
    const isZero = integer === '0' && !/[1-9]/.test(fraction);
    return {
        negative: decimal.negative && !isZero,
        integer,
        fraction
    };
}

export function formatServerNumber(value, fractionDigits) {
    const decimal = roundedNumberParts(value, fractionDigits, true);
    return (decimal.negative ? '-' : '')
        + decimal.integer
        + (fractionDigits === 0 ? '' : '.' + decimal.fraction);
}

export function formatDecimalNumber(value, fractionDigits) {
    const decimal = roundedNumberParts(value, fractionDigits, false);
    return (decimal.negative ? '-' : '')
        + decimal.integer
        + (fractionDigits === 0 ? '' : '.' + decimal.fraction);
}

export function limitServerNumber(value) {
    const decimal = roundedNumberParts(value, SERVER_DOUBLE_FRACTION_DIGITS, true);
    let fraction = decimal.fraction;
    fraction = fraction.replace(/0+$/, '');
    if (decimal.integer === '0' && fraction === '') {
        return '0';
    }
    return (decimal.negative ? '-' : '')
        + decimal.integer
        + (fraction === '' ? '' : '.' + fraction);
}

function incrementDecimal(integer, fraction, scale) {
    const digits = (integer + fraction.padEnd(scale, '0')).split('');
    let carry = 1;
    for (let index = digits.length - 1; index >= 0 && carry === 1; index--) {
        const nextDigit = Number(digits[index]) + carry;
        digits[index] = String(nextDigit % 10);
        carry = nextDigit >= 10 ? 1 : 0;
    }
    if (carry === 1) {
        digits.unshift('1');
    }
    const integerLength = digits.length - scale;
    return {
        integer: digits.slice(0, integerLength).join(''),
        fraction: digits.slice(integerLength).join('')
    };
}

export const MAX_SERVER_DOUBLE_ABS = MAX_SERVER_DOUBLE_ABS_TEXT;
